import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminCart.css';

const AdminCart = () => {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCart, setSelectedCart] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCarts();
  }, []);

  const fetchCarts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/carts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter only carts that have items
          const cartsWithItems = (data.data || []).filter(cart => {
            const items = getCartItems(cart);
            return items && items.length > 0;
          });
          setCarts(cartsWithItems);
        } else {
          setError(data.message || 'Failed to fetch carts');
        }
      } else {
        setError('Failed to fetch carts');
      }
    } catch (error) {
      console.error('Error fetching carts:', error);
      setError('Error loading carts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatPrice = (price) => {
    if (!price) return '₹0.00';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const calculateTotal = (cartItems) => {
    if (!cartItems || cartItems.length === 0) return 0;
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Get cart items with proper structure
  const getCartItems = (cart) => {
    if (cart.items && Array.isArray(cart.items)) {
      return cart.items;
    }
    if (cart.cart_items && Array.isArray(cart.cart_items)) {
      return cart.cart_items;
    }
    return [];
  };

  // Filter carts based on search term
  const filteredCarts = carts.filter(cart => {
    const userName = cart.user_name || cart.user?.name || 'Guest User';
    const userEmail = cart.user_email || cart.user?.email || '';
    const searchLower = searchTerm.toLowerCase();
    return userName.toLowerCase().includes(searchLower) || 
           userEmail.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="admin-cart-loading">
        <div className="spinner"></div>
        <p>Loading carts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-cart-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-cart">
      <div className="admin-cart-header">
        <h1>User Carts Management</h1>
        
        {/* Search Box - Only show if there are carts */}
        {carts.length > 0 && (
          <div className="search-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by user name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm('')}>
                  ×
                </button>
              )}
            </div>
            <button className="refresh-btn" onClick={fetchCarts}>
              ⟳ Refresh
            </button>
          </div>
        )}
      </div>

      {/* Carts List */}
      <div className="carts-list">
        {carts.length === 0 ? (
          <div className="no-data">
            <div className="empty-cart-icon">🛒</div>
            <h3>No Carts Found</h3>
            <p>No users have added any items to their cart yet.</p>
            <p className="empty-cart-hint">Carts will appear here when users add products to their shopping cart.</p>
          </div>
        ) : filteredCarts.length === 0 ? (
          <div className="no-data">
            <div className="empty-cart-icon">🔍</div>
            <h3>No Results Found</h3>
            <p>No carts match your search criteria.</p>
            <button className="clear-filter-btn" onClick={() => setSearchTerm('')}>
              Clear Search
            </button>
          </div>
        ) : (
          filteredCarts.map(cart => {
            const cartItems = getCartItems(cart);
            const total = calculateTotal(cartItems);
            const userName = cart.user_name || cart.user?.name || 'Guest User';
            const userEmail = cart.user_email || cart.user?.email || 'No email';
            
            return (
              <div key={cart.id} className="cart-card">
                <div className="cart-header" onClick={() => setSelectedCart(selectedCart === cart.id ? null : cart.id)}>
                  <div className="cart-info">
                    <div className="user-info">
                      <span className="user-icon">👤</span>
                      <div>
                        <div className="user-name">{userName}</div>
                        <div className="user-email">{userEmail}</div>
                      </div>
                    </div>
                    <div className="cart-stats">
                      <div className="stat">
                        <span className="stat-label">Items:</span>
                        <span className="stat-value">{cartItems.length}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total:</span>
                        <span className="stat-value total">{formatPrice(total)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Last Updated:</span>
                        <span className="stat-value">{formatDate(cart.updated_at || cart.created_at)}</span>
                      </div>
                    </div>
                    <div className="expand-icon">
                      {selectedCart === cart.id ? '▲' : '▼'}
                    </div>
                  </div>
                </div>
                
                {selectedCart === cart.id && (
                  <div className="cart-details">
                    <table className="cart-items-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Variant</th>
                          <th>Size</th>
                          <th>Lens</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartItems.map((item, index) => (
                          <tr key={item.id || index}>
                            <td className="product-cell">
                              <div className="product-info">
                                {item.product_image && (
                                  <img 
                                    src={item.product_image} 
                                    alt={item.product_name}
                                    className="product-thumb"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                                    }}
                                  />
                                )}
                                <div className="product-name">{item.product_name || 'Unknown Product'}</div>
                              </div>
                            </td>
                            <td className="variant-cell">
                              {item.variant_name || item.variant_color || '-'}
                            </td>
                            <td className="size-cell">
                              {item.size_name || item.size_details || '-'}
                            </td>
                            <td className="lens-cell">
                              {item.lens_name || '-'}
                              {item.lens_color_name && ` (${item.lens_color_name})`}
                            </td>
                            <td className="quantity-cell">{item.quantity}</td>
                            <td className="price-cell">{formatPrice(item.price)}</td>
                            <td className="total-cell">{formatPrice(item.price * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="6" className="subtotal-label">Subtotal:</td>
                          <td className="subtotal-value">{formatPrice(total)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    
                    {/* Prescription Details if available */}
                    {cartItems.some(item => item.prescription) && (
                      <div className="prescriptions-section">
                        <h4>Prescription Details</h4>
                        {cartItems.map((item, idx) => (
                          item.prescription && (
                            <div key={idx} className="prescription-card">
                              <div className="prescription-product">{item.product_name}</div>
                              <div className="prescription-details">
                                <div className="prescription-eye">
                                  <strong>Right Eye:</strong>
                                  <span>SPH: {item.prescription.right?.sph || '-'}</span>
                                  <span>CYL: {item.prescription.right?.cyl || '-'}</span>
                                  <span>Axis: {item.prescription.right?.axis || '-'}</span>
                                  <span>ADD: {item.prescription.right?.add || '-'}</span>
                                  <span>PD: {item.prescription.right?.pd || '-'}</span>
                                </div>
                                <div className="prescription-eye">
                                  <strong>Left Eye:</strong>
                                  <span>SPH: {item.prescription.left?.sph || '-'}</span>
                                  <span>CYL: {item.prescription.left?.cyl || '-'}</span>
                                  <span>Axis: {item.prescription.left?.axis || '-'}</span>
                                  <span>ADD: {item.prescription.left?.add || '-'}</span>
                                  <span>PD: {item.prescription.left?.pd || '-'}</span>
                                </div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer with count - Only show if there are carts */}
      {carts.length > 0 && (
        <div className="carts-footer">
          <div className="carts-count">
            Showing {filteredCarts.length} of {carts.length} active cart{carts.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCart;